import { Directionality } from '@angular/cdk/bidi';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  QueryList,
  Self,
  ViewChildren,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { SelectOption } from 'src/app/@shared/interfaces/select-option.interface';

@Component({
    selector: 'mot-button-toggle',
    templateUrl: './mot-button-toggle.component.html',
    styleUrls: ['./mot-button-toggle.component.scss'],
    standalone: false
})
export class MotButtonToggleComponent
  implements ControlValueAccessor, OnInit, OnDestroy
{
  @Input() options: Observable<SelectOption<any>[]>;
  @Input() value: string;
  @Input() class: string;
  @Input() toggleClass: string;
  /** Accessible name for the radio group (required for WCAG radiogroup pattern). */
  @Input() groupAriaLabel: string;

  @ViewChildren('radioBtn') radioButtons!: QueryList<ElementRef<HTMLElement>>;

  formControl: FormControl;
  optionsList: SelectOption<any>[] = [];
  private optionsSub = Subscription.EMPTY;

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    private readonly cdr: ChangeDetectorRef,
    @Optional() private readonly dir?: Directionality
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit() {
    if (this.ngControl) {
      this.formControl = this.ngControl.control as FormControl;
      if (this.value) {
        this.formControl.setValue(this.value);
      }
    }
    if (this.options) {
      this.optionsSub = this.options.subscribe((opts) => {
        this.optionsList = opts ?? [];
        this.cdr.markForCheck();
      });
    }
  }

  ngOnDestroy(): void {
    this.optionsSub.unsubscribe();
  }

  get isGroupDisabled(): boolean {
    return !!this.formControl?.disabled;
  }

  isSelected(opt: SelectOption<any>): boolean {
    const v = this.formControl?.value;
    // Loose equality: car-integrated form uses boolean isFirstLeg (true/false) vs enum (1/0).
    return v == null ? opt.value == null : v == opt.value;
  }

  tabIndexForOption(opt: SelectOption<any>): number {
    if (this.isGroupDisabled || opt.disabled) {
      return -1;
    }
    const selected = this.optionsList.find(
      (o) => !o.disabled && this.isSelected(o)
    );
    if (selected) {
      return this.isSelected(opt) ? 0 : -1;
    }
    const firstEnabled = this.optionsList.find((o) => !o.disabled);
    return opt === firstEnabled ? 0 : -1;
  }

  selectOption(opt: SelectOption<any>): void {
    if (opt.disabled || this.isGroupDisabled) {
      return;
    }
    this.formControl?.setValue(opt.value);
    this.formControl?.markAsTouched();
    this.onChange(opt.value);
    this.onTouched();
  }

  onRadioKeydown(event: KeyboardEvent, index: number): void {
    if (this.isGroupDisabled) {
      return;
    }
    const key = event.key;
    const isRtl = this.dir?.value === 'rtl';
    let delta = 0;
    let handled = false;

    if (key === 'ArrowRight') {
      delta = isRtl ? -1 : 1;
      handled = true;
    } else if (key === 'ArrowLeft') {
      delta = isRtl ? 1 : -1;
      handled = true;
    } else if (key === 'ArrowDown') {
      delta = 1;
      handled = true;
    } else if (key === 'ArrowUp') {
      delta = -1;
      handled = true;
    } else if (key === 'Home') {
      handled = true;
      const next = this.firstEnabledIndex();
      if (next >= 0) {
        event.preventDefault();
        this.selectOption(this.optionsList[next]);
        this.focusRadioAt(next);
      }
      return;
    } else if (key === 'End') {
      handled = true;
      const next = this.lastEnabledIndex();
      if (next >= 0) {
        event.preventDefault();
        this.selectOption(this.optionsList[next]);
        this.focusRadioAt(next);
      }
      return;
    } else if (key === ' ' || key === 'Enter') {
      event.preventDefault();
      const opt = this.optionsList[index];
      if (opt && !opt.disabled) {
        this.selectOption(opt);
      }
      return;
    }

    if (handled && delta !== 0) {
      event.preventDefault();
      const next = this.findNextEnabledIndex(index, delta);
      if (next !== index) {
        this.selectOption(this.optionsList[next]);
        this.focusRadioAt(next);
      }
    }
  }

  private firstEnabledIndex(): number {
    return this.optionsList.findIndex((o) => !o.disabled);
  }

  private lastEnabledIndex(): number {
    for (let i = this.optionsList.length - 1; i >= 0; i--) {
      if (!this.optionsList[i].disabled) {
        return i;
      }
    }
    return -1;
  }

  private findNextEnabledIndex(from: number, delta: number): number {
    const len = this.optionsList.length;
    if (len === 0) {
      return from;
    }
    let i = from;
    for (let step = 0; step < len; step++) {
      i = (i + delta + len) % len;
      if (!this.optionsList[i]?.disabled) {
        return i;
      }
    }
    return from;
  }

  private focusRadioAt(index: number): void {
    this.cdr.detectChanges();
    setTimeout(() => {
      const el = this.radioButtons?.get(index)?.nativeElement;
      el?.focus();
    });
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (!!this.formControl) {
      if (isDisabled) {
        this.formControl.disable({ emitEvent: false });
      } else {
        this.formControl.enable({ emitEvent: false });
      }
    }
  }
}
